/**
 * processTrainSimulator.ts — Process Train Compliance Simulation Engine
 *
 * Simulates water quality step-by-step through each treatment unit using
 * established physics, chemistry, and mathematics from:
 *   - Metcalf & Eddy: Wastewater Engineering, 5th Ed. (2014)
 *   - WEF Manual of Practice No. 8 — Activated Sludge
 *   - WEF Manual of Practice No. 36 — Membrane Bioreactors
 *   - EPA Process Design Manual — Suspended Solids Removal
 *   - Lawrence & McCarty kinetic model (J. Sanit. Engrg. Div. ASCE, 1970)
 *   - Stokes' Law for gravitational settling
 *   - Henry's Law for dissolved air flotation
 *   - Freundlich isotherm for GAC adsorption
 *   - IAWQ Activated Sludge Models (ASM1)
 *
 * Returns stage-by-stage water quality, compliance projection against active
 * discharge standard, and remediation recommendations for any gaps.
 */

import { type StandardCode, type WastewaterStandard, ALL_STANDARDS } from '@/types/standards';

// ─── Water Quality Profile ──────────────────────────────────────────────────

export interface WaterQuality {
  bod5:            number;   // mg/L O₂
  cod:             number;   // mg/L O₂
  tss:             number;   // mg/L
  oilGrease:       number;   // mg/L
  ammoniaN:        number;   // mg/L N
  totalNitrogen:   number;   // mg/L N
  totalPhosphorus: number;   // mg/L P
  tds:             number;   // mg/L
  ph:              number;   // —
  temperature:     number;   // °C
  conductivity:    number;   // µS/cm
  lead:            number;   // mg/L
  mercury:         number;   // mg/L
  cadmium:         number;   // mg/L
  chromium:        number;   // mg/L
  nickel:          number;   // mg/L
  zinc:            number;   // mg/L
  copper:          number;   // mg/L
  phenol:          number;   // mg/L
  sulfides:        number;   // mg/L
  surfactants:     number;   // mg/L
  color:           number;   // Pt-Co
  turbidity:       number;   // NTU
}

export const WQ_META: Record<keyof WaterQuality, { label: string; unit: string }> = {
  bod5:            { label: 'BOD₅',            unit: 'mg/L' },
  cod:             { label: 'COD',              unit: 'mg/L' },
  tss:             { label: 'TSS',              unit: 'mg/L' },
  oilGrease:       { label: 'Oil & Grease',     unit: 'mg/L' },
  ammoniaN:        { label: 'Ammonia-N',        unit: 'mg/L N' },
  totalNitrogen:   { label: 'Total Nitrogen',   unit: 'mg/L N' },
  totalPhosphorus: { label: 'Total Phosphorus', unit: 'mg/L P' },
  tds:             { label: 'TDS',              unit: 'mg/L' },
  ph:              { label: 'pH',               unit: '' },
  temperature:     { label: 'Temperature',      unit: '°C' },
  conductivity:    { label: 'Conductivity',     unit: 'µS/cm' },
  lead:            { label: 'Lead (Pb)',         unit: 'mg/L' },
  mercury:         { label: 'Mercury (Hg)',      unit: 'mg/L' },
  cadmium:         { label: 'Cadmium (Cd)',      unit: 'mg/L' },
  chromium:        { label: 'Chromium (Cr)',     unit: 'mg/L' },
  nickel:          { label: 'Nickel (Ni)',       unit: 'mg/L' },
  zinc:            { label: 'Zinc (Zn)',         unit: 'mg/L' },
  copper:          { label: 'Copper (Cu)',       unit: 'mg/L' },
  phenol:          { label: 'Phenol',            unit: 'mg/L' },
  sulfides:        { label: 'Sulfides',          unit: 'mg/L' },
  surfactants:     { label: 'Surfactants',       unit: 'mg/L' },
  color:           { label: 'Colour',            unit: 'Pt-Co' },
  turbidity:       { label: 'Turbidity',         unit: 'NTU' },
};

// ─── Stage Result ───────────────────────────────────────────────────────────

export interface ChemicalDosing {
  chemical: string;
  dose: string;
  purpose: string;
}

export interface StageResult {
  equipmentKey:    string;
  equipmentLabel:  string;
  stage:           'pre-treatment' | 'primary' | 'secondary' | 'tertiary' | 'polishing' | 'sludge';
  influent:        WaterQuality;
  effluent:        WaterQuality;
  removals:        Partial<Record<keyof WaterQuality, number>>; // % removal
  dosing:          ChemicalDosing[];
  oxygenDemandKgd: number;   // kg O₂/day (aeration only)
  sludgeKgd:       number;   // kg TSS/day generated
  engineeringNotes: string[];
  warnings:        string[];
}

// ─── Compliance Result ──────────────────────────────────────────────────────

export interface TrainComplianceResult {
  param:       keyof WaterQuality;
  label:       string;
  unit:        string;
  finalValue:  number;
  limit:       number;
  pass:        boolean;
  ratio:       number;         // finalValue / limit
  margin:      number;         // % under (+) or over (-) limit
  status:      'compliant' | 'near_limit' | 'exceeded' | 'critical';
  color:       string;
}

// ─── Gap & Remediation ──────────────────────────────────────────────────────

export interface AlternativeProcess {
  key:             string;
  name:            string;
  stage:           string;
  removalPct:      number;
  projectedValue:  number;    // mg/L after this process
  achieves:        boolean;   // does it achieve the limit?
  chemistry:       string;    // scientific principle / reaction
  designNote:      string;
  capexBand:       'low' | 'medium' | 'high' | 'very_high';
  opexBand:        'low' | 'medium' | 'high' | 'very_high';
}

export interface ComplianceGap {
  param:           keyof WaterQuality;
  label:           string;
  unit:            string;
  finalValue:      number;
  limit:           number;
  exceedance:      number;    // mg/L above limit
  exceedancePct:   number;    // % over limit
  requiredRemoval: number;    // additional % removal needed from final effluent
  alternatives:    AlternativeProcess[];
}

// ─── Full Simulation Result ─────────────────────────────────────────────────

export interface TrainSimulation {
  influent:        WaterQuality;
  stages:          StageResult[];
  finalEffluent:   WaterQuality;
  standardCode:    StandardCode;
  standardName:    string;
  compliance:      TrainComplianceResult[];
  overallCompliant: boolean;
  complianceScore: number;    // 0–100 (% params passing)
  gaps:            ComplianceGap[];
  totalSludgeKgd:  number;
  totalO2Kgd:      number;
  totalDosingChems: ChemicalDosing[];
  warnings:        string[];
  summary:         string;
}

// ─── Parameter mapping from raw sample params ──────────────────────────────

export function rawParamsToWQ(p: Record<string, number>): WaterQuality {
  const g = (keys: string[], def = 0) => {
    for (const k of keys) {
      const v = p[k] ?? p[k.toLowerCase()] ?? p[k.toUpperCase()];
      if (v !== undefined && v !== null) return v;
    }
    return def;
  };
  return {
    bod5:            g(['bod5', 'bod', 'BOD5', 'BOD']),
    cod:             g(['cod', 'COD']),
    tss:             g(['tss', 'TSS']),
    oilGrease:       g(['oilGrease', 'oil_grease', 'og', 'OG', 'oil']),
    ammoniaN:        g(['ammoniaN', 'ammoniaн', 'nh3n', 'nh3', 'NH3N']),
    totalNitrogen:   g(['totalNitrogen', 'total_nitrogen', 'tn', 'TN']),
    totalPhosphorus: g(['totalPhosphorus', 'total_phosphorus', 'tp', 'TP']),
    tds:             g(['tds', 'TDS']),
    ph:              g(['ph', 'pH'], 7.0),
    temperature:     g(['temperature', 'temp'], 20),
    conductivity:    g(['conductivity', 'cond'], 0),
    lead:            g(['lead', 'Pb', 'pb']),
    mercury:         g(['mercury', 'Hg', 'hg']),
    cadmium:         g(['cadmium', 'Cd', 'cd']),
    chromium:        g(['chromium', 'Cr', 'cr']),
    nickel:          g(['nickel', 'Ni', 'ni']),
    zinc:            g(['zinc', 'Zn', 'zn']),
    copper:          g(['copper', 'Cu', 'cu']),
    phenol:          g(['phenol']),
    sulfides:        g(['sulfides', 'sulphides', 'h2s']),
    surfactants:     g(['surfactants', 'anionic_surfactants', 'mbas']),
    color:           g(['color', 'colour', 'color_ptco']),
    turbidity:       g(['turbidity', 'ntu']),
  };
}

// ─── Helper: apply fractional removal ─────────────────────────────────────

function applyRemoval(value: number, pct: number): number {
  return Math.max(0, value * (1 - pct / 100));
}

function clamp(v: number, min: number, max: number) {
  return Math.max(min, Math.min(max, v));
}

function copyWQ(q: WaterQuality): WaterQuality {
  return { ...q };
}

// ─── Equipment simulation functions ────────────────────────────────────────
// Each returns { effluent, removals, dosing, notes, warnings, sludge, o2 }

interface SimOut {
  effluent:    WaterQuality;
  removals:    Partial<Record<keyof WaterQuality, number>>;
  dosing:      ChemicalDosing[];
  notes:       string[];
  warnings:    string[];
  sludgeKgd:   number;
  o2Kgd:       number;
}

// 1. Screening ─────────────────────────────────────────────────────────────
function simScreening(q: WaterQuality): SimOut {
  // Coarse (6mm aperture): TSS 10–20%, BOD 5–15% (attached to solids)
  // Fine (1–3mm): TSS 15–30%, BOD 10–20%
  const tssRem = q.tss > 300 ? 25 : 15;   // finer screen at higher SS
  const bodRem = tssRem * 0.5;             // BOD proportional to particulate fraction
  const eff = copyWQ(q);
  eff.tss = applyRemoval(q.tss, tssRem);
  eff.bod5 = applyRemoval(q.bod5, bodRem);
  // Grit/large particles carry some COD
  eff.cod = applyRemoval(q.cod, bodRem * 0.7);
  const sludgeKgd = (q.tss - eff.tss) * 0.001; // nominal (not a defined flow)
  const notes = [`Screen aperture ${q.tss > 300 ? '3 mm fine' : '6 mm coarse'} recommended. TSS removal ${tssRem}%.`];
  return { effluent: eff, removals: { tss: tssRem, bod5: bodRem }, dosing: [], notes, warnings: [], sludgeKgd, o2Kgd: 0 };
}

// 2. Equalization ──────────────────────────────────────────────────────────
function simEqualization(q: WaterQuality): SimOut {
  // EQ dampens peaks — quality unchanged on average. Slight passive TSS settling (quiescent zones).
  const eff = copyWQ(q);
  eff.tss = applyRemoval(q.tss, 8);         // ~8% gravity settling in EQ
  eff.bod5 = applyRemoval(q.bod5, 5);        // solids-bound BOD
  const notes = ['Equalization averages diurnal load variation. Peak:average BOD load ratio reduced ~60%.', 'Passive settling removes ~8% settleable solids.'];
  return { effluent: eff, removals: { tss: 8, bod5: 5 }, dosing: [], notes, warnings: [], sludgeKgd: 0.001, o2Kgd: 0 };
}

// 3. pH Adjustment ─────────────────────────────────────────────────────────
function simPHAdjustment(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const dosing: ChemicalDosing[] = [];
  const notes: string[] = [];
  const warnings: string[] = [];
  const removals: Partial<Record<keyof WaterQuality, number>> = {};

  const targetPH = 7.2;

  if (q.ph < 6.0) {
    const doseMgL = Math.round((6.5 - q.ph) * 180); // NaOH demand: ~180 mg/L per pH unit (approximate)
    dosing.push({ chemical: 'NaOH (caustic soda)', dose: `${doseMgL} mg/L`, purpose: `Raise pH from ${q.ph.toFixed(1)} to target 7.2` });
    eff.ph = targetPH;
    notes.push(`Acidic influent (pH ${q.ph.toFixed(1)}) — NaOH dosing required for biological treatment range.`);
  } else if (q.ph > 9.5) {
    const doseMgL = Math.round((q.ph - 8.5) * 50); // H₂SO₄ demand
    dosing.push({ chemical: 'H₂SO₄ (sulphuric acid)', dose: `${doseMgL} mg/L`, purpose: `Lower pH from ${q.ph.toFixed(1)} to target 7.2` });
    eff.ph = targetPH;
    notes.push(`Alkaline influent (pH ${q.ph.toFixed(1)}) — H₂SO₄ dosing required.`);
    // Heavy metal precipitation effective above pH 9.0
    const metals: Array<keyof WaterQuality> = ['lead', 'chromium', 'cadmium', 'zinc', 'nickel', 'copper'];
    metals.forEach(m => {
      const rem = m === 'chromium' ? 85 : m === 'lead' ? 80 : 75;
      eff[m as keyof WaterQuality] = applyRemoval(q[m as keyof WaterQuality] as number, rem) as any;
      removals[m as keyof WaterQuality] = rem;
    });
    notes.push('High pH (>9.0): heavy metal hydroxide precipitation co-occurs — Pb, Cr, Cd, Zn, Ni, Cu removal 75–85%.');
  } else if (q.ph >= 8.5 && q.ph <= 9.5) {
    // Moderate alkaline — lime precipitation for metals
    dosing.push({ chemical: 'Ca(OH)₂ (hydrated lime)', dose: '150–250 mg/L', purpose: 'pH stabilisation and heavy metal precipitation' });
    eff.ph = clamp(q.ph, 6.5, 9.0);
    const metals: Array<keyof WaterQuality> = ['lead', 'chromium', 'cadmium', 'zinc'];
    metals.forEach(m => {
      eff[m as keyof WaterQuality] = applyRemoval(q[m as keyof WaterQuality] as number, 60) as any;
      removals[m as keyof WaterQuality] = 60;
    });
    notes.push('Lime dosing precipitates divalent metal hydroxides. M(OH)₂ ↓ + Ca²⁺ → solids removed in downstream clarifier.');
  } else {
    eff.ph = q.ph; // in range, no adjustment
    notes.push(`pH ${q.ph.toFixed(1)} is within acceptable biological operating range (6.5–9.0). No dosing required.`);
  }

  // Sulfide oxidation at adjusted pH if aerated
  if (q.sulfides > 1.0 && eff.ph < 8.0) {
    notes.push(`Sulfide H₂S stripping enhanced at pH < 8.0 — consider aeration pre-treatment for sulfide ${q.sulfides} mg/L.`);
    warnings.push(`H₂S gas release risk — enclosed pH adjustment tank with fume extraction required.`);
  }

  return { effluent: eff, removals, dosing, notes, warnings, sludgeKgd: 0.05, o2Kgd: 0 };
}

// 4. Buffer Tank ───────────────────────────────────────────────────────────
function simBuffer(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  return {
    effluent: eff, removals: {}, dosing: [],
    notes: ['Buffer tank provides surge protection and process stability. No pollutant removal.'],
    warnings: [], sludgeKgd: 0, o2Kgd: 0,
  };
}

// 5. DAF ───────────────────────────────────────────────────────────────────
// Physics: Henry's Law — dissolved air released as microbubbles (20–100 µm)
// at atmospheric pressure. Bubble rise velocity ≈ Stokes' Law.
// A:S ratio = 1.3 × Sa × (f×P - 1) / (SS × 1000)
function simDAF(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const dosing: ChemicalDosing[] = [];
  const notes: string[] = [];
  const warnings: string[] = [];

  // Coagulant enhances performance — recommended when O&G > 25 or TSS > 100
  const useCoag = q.oilGrease > 25 || q.tss > 100;
  if (useCoag) {
    dosing.push({ chemical: 'Alum (Al₂(SO₄)₃)', dose: '150–350 mg/L', purpose: 'Coagulation — charge neutralisation to enhance flotation' });
    dosing.push({ chemical: 'Anionic/cationic polymer', dose: '2–8 mg/L', purpose: 'Flocculation — bridging to form floatable flocs' });
  }

  // TSS removal: 70–95%. Enhanced with coagulant to 85–95%
  const tssRem = useCoag ? clamp(70 + (q.tss / 20), 80, 95) : clamp(50 + (q.tss / 25), 65, 88);
  // O&G: Henry's Law governs bubble attachment to hydrophobic surfaces — 85–98%
  const ogRem  = clamp(75 + (q.oilGrease / 20), 82, 98);
  // BOD: primarily particulate fraction
  const bodRem = clamp(15 + tssRem * 0.3, 20, 52);
  // COD: similar but lower (dissolved refractory COD unaffected)
  const codRem = clamp(10 + tssRem * 0.25, 15, 42);
  // TP: chemical precipitation with coagulant
  const tpRem  = useCoag ? clamp(40 + q.totalPhosphorus * 2, 55, 85) : 30;
  // Surfactants: foam-forming surfactants removed by flotation
  const surfRem = clamp(30 + q.surfactants * 2, 35, 65);

  eff.tss           = applyRemoval(q.tss, tssRem);
  eff.oilGrease     = applyRemoval(q.oilGrease, ogRem);
  eff.bod5          = applyRemoval(q.bod5, bodRem);
  eff.cod           = applyRemoval(q.cod, codRem);
  eff.totalPhosphorus = applyRemoval(q.totalPhosphorus, tpRem);
  eff.surfactants   = applyRemoval(q.surfactants, surfRem);

  // Sludge: float sludge at ~3–5% DS (assume ~25% removal as wet float)
  const sludgeKgd = (q.tss - eff.tss) * 4; // float has ~4× the TSS of removed solids (dilution factor)

  notes.push(`DAF operating at hydraulic loading 5–10 m/h, recycle ratio 15–25%, pressure 5–6 bar.`);
  notes.push(`Henry's Law: air solubility at 6 bar ≈ 105 mg/L. Microbubbles 20–100 µm provide rise velocity 0.5–5 m/h (Stokes).`);
  if (useCoag) notes.push(`Coagulant (alum) dose drives charge neutralisation: Al³⁺ + 3OH⁻ → Al(OH)₃↓. Polymer bridges flocs for flotation.`);
  if (q.oilGrease > 100) warnings.push(`Very high O&G (${q.oilGrease} mg/L) — risk of float skimmer overload. Consider two-stage flotation.`);
  if (q.ph < 6 || q.ph > 9) warnings.push(`pH ${q.ph} outside optimal coagulation range (6–8.5) — pH adjustment strongly recommended ahead of DAF.`);

  return {
    effluent: eff,
    removals: { tss: tssRem, oilGrease: ogRem, bod5: bodRem, cod: codRem, totalPhosphorus: tpRem, surfactants: surfRem },
    dosing, notes, warnings, sludgeKgd, o2Kgd: 0,
  };
}

// 6. Lamella Clarifier ─────────────────────────────────────────────────────
// Stokes' Law: vs = (ρp - ρw) × g × dp² / 18µ
// Effective surface area × (1 / cos θ) per Yao (1970)
function simLamella(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const notes: string[] = [];
  const warnings: string[] = [];

  // Settling velocity for TSS (typical ρ = 1050–1200 kg/m³, dp = 10–100 µm)
  // Effective SOR: 1.0–1.5 m/h surface equivalent; lamella multiplies by L×sinθ
  const tssRem = q.tss > 800 ? 65 : q.tss > 200 ? 80 : 70;
  const bodRem = tssRem * 0.35;  // BOD mostly particulate
  const codRem = tssRem * 0.25;

  eff.tss  = applyRemoval(q.tss, tssRem);
  eff.bod5 = applyRemoval(q.bod5, bodRem);
  eff.cod  = applyRemoval(q.cod, codRem);

  // Sludge from settled solids
  const sludgeKgd = (q.tss - eff.tss) * 2.5;

  notes.push(`Stokes' Law settling: vs = (ρ_p - ρ_w)·g·d_p² / 18µ. Lamella plates (60° incline) multiply surface by 3–5×.`);
  notes.push(`Surface overflow rate 0.5–1.5 m/h (plate equivalent). Solids loading ≤5 kg/m²/h.`);
  if (q.tss > 1000) warnings.push(`TSS ${q.tss} mg/L very high — underflow thickening required. Risk of plate fouling.`);
  if (q.oilGrease > 50) warnings.push(`O&G ${q.oilGrease} mg/L — floating grease will reverse settle and foul inclined plates. DAF preferred for high FOG.`);

  return { effluent: eff, removals: { tss: tssRem, bod5: bodRem, cod: codRem }, dosing: [], notes, warnings, sludgeKgd, o2Kgd: 0 };
}

// 7. Aeration Tank (Activated Sludge) ──────────────────────────────────────
// Lawrence-McCarty model (1970):
//   Se = Ks(1 + kd·θc) / [θc(µmax - kd) - 1]
//   µmax = 4–6 /day, Ks = 60–80 mg/L BOD, kd = 0.05 /day
// Nitrification: NH₃-N → NO₃-N requires SRT > 8–15 days at 20°C
// O₂ demand: 1.0 kg O₂/kg BOD removed + 4.57 kg O₂/kg NH₃-N nitrified
// Sludge: Px = Y·Q·(S₀-Se) - kd·MLVSS·V
function simAeration(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const dosing: ChemicalDosing[] = [];
  const notes: string[] = [];
  const warnings: string[] = [];

  // ── BOD removal — Lawrence-McCarty kinetics ──
  const SRT = 12; // days — default design SRT (supports nitrification)
  const HRT_h = 8; // hours — typical ASP HRT
  const muMax = 5.0; // /day
  const Ks    = 70;  // mg/L BOD
  const kd    = 0.05;// /day
  const Y     = 0.6; // g VSS / g BOD
  // Se (effluent BOD) from L-M model:
  const Se_bod = Math.max(2, Ks * (1 + kd * SRT) / (SRT * (muMax - kd) - 1));
  const bodRem = q.bod5 > 0 ? clamp(((q.bod5 - Se_bod) / q.bod5) * 100, 0, 99) : 95;

  // ── COD removal — refractory fraction stays ──
  const bodCodRatio = q.cod > 0 ? q.bod5 / q.cod : 0;
  const refractoryCOD = q.cod * Math.max(0, 0.4 - bodCodRatio); // refractory increases as BOD:COD falls
  const codEff = Math.max(refractoryCOD + 15, q.cod * (1 - bodRem / 100 * 0.9));
  const codRem = q.cod > 0 ? clamp(((q.cod - codEff) / q.cod) * 100, 0, 95) : 80;

  // ── TSS removal — MLSS in settled effluent ~~15–20 mg/L ──
  const tssRem = clamp(75 + (q.tss - 200) * 0.02, 75, 92);

  // ── Nitrification — Nitrosomonas at SRT 12d, T=20°C ──
  const nitrifyActive = SRT >= 10 && q.ph >= 6.5 && q.ph <= 8.5;
  const nh3Rem = nitrifyActive ? clamp(85 + q.ammoniaN * 0.1, 88, 99) : clamp(20 + q.ammoniaN * 0.1, 20, 40);
  if (!nitrifyActive) {
    warnings.push('Nitrification limited — pH outside 6.5–8.5 range or temperature <12°C. Consider SRT extension.');
  }

  // ── TN — partial denitrification if anoxic zone ──
  const tnRem = nitrifyActive ? 35 : 10; // denitrification requires anoxic zone
  if (nitrifyActive && q.totalNitrogen > 30) {
    dosing.push({ chemical: 'Anoxic zone (internal recycle)', dose: '3–5× Q', purpose: 'Pre-anoxic denitrification (Ludzack-Ettinger configuration)' });
    notes.push('Pre-anoxic denitrification via Ludzack-Ettinger (LE) configuration — NO₃ recycle 3–5× Q. Achieves 40–65% TN removal.');
  }

  // ── TP — biological uptake only (EBPR needs anaerobic zone) ──
  const tpRem = 20; // luxury uptake only without anaerobic selector
  notes.push('Biological P removal (EBPR) requires anaerobic selector zone — luxury P uptake without it ≈ 20% only.');

  // ── Alkalinity consumption by nitrification: 7.14 mg CaCO₃ / mg NH₃-N ──
  const alkaConsumed = (q.ammoniaN * nh3Rem / 100) * 7.14;
  if (q.ph < 7.2 && alkaConsumed > 100) {
    dosing.push({ chemical: 'NaHCO₃ (sodium bicarbonate)', dose: `${Math.round(alkaConsumed * 1.2)} mg/L`, purpose: 'Alkalinity supplement for nitrification pH stability (7.14 mg/mg NH₃-N)' });
    warnings.push(`Nitrification will consume ≈${Math.round(alkaConsumed)} mg/L CaCO₃ alkalinity — risk of pH crash below 6.5 without supplementation.`);
  }

  // ── Toxicity inhibition check ──
  const toxicParams: Array<{ key: keyof WaterQuality; threshold: number; name: string }> = [
    { key: 'lead', threshold: 0.1, name: 'Lead' }, { key: 'copper', threshold: 0.5, name: 'Copper' },
    { key: 'nickel', threshold: 1.0, name: 'Nickel' }, { key: 'phenol', threshold: 20, name: 'Phenol' },
    { key: 'sulfides', threshold: 5, name: 'Sulfides' }, { key: 'chromium', threshold: 0.5, name: 'Chromium' },
  ];
  toxicParams.forEach(({ key, threshold, name }) => {
    const val = q[key] as number;
    if (val > threshold) {
      warnings.push(`INHIBITION RISK: ${name} ${val} mg/L exceeds biological inhibition threshold (${threshold} mg/L) — biomass may be severely impacted. Pre-treat before biological stage.`);
    }
  });

  // ── O₂ demand ──
  const bodRemoved = q.bod5 - applyRemoval(q.bod5, bodRem);
  const nh3Nitrified = nitrifyActive ? q.ammoniaN * nh3Rem / 100 : 0;
  const o2Kgd = bodRemoved * 1.0 * 0.001 + nh3Nitrified * 4.57 * 0.001; // per m³ influent

  // ── Sludge production: Px = Y × ΔS - kd × MLVSS ──
  const sludgeKgd = (Y * bodRemoved - kd * 2500) * 0.001;

  // Apply removals
  eff.bod5          = Math.max(2, applyRemoval(q.bod5, bodRem));
  eff.cod           = codEff;
  eff.tss           = applyRemoval(q.tss, tssRem);
  eff.ammoniaN      = applyRemoval(q.ammoniaN, nh3Rem);
  eff.totalNitrogen = applyRemoval(q.totalNitrogen, tnRem);
  eff.totalPhosphorus = applyRemoval(q.totalPhosphorus, tpRem);
  eff.sulfides      = applyRemoval(q.sulfides, 70); // H₂S oxidised by dissolved O₂
  eff.surfactants   = applyRemoval(q.surfactants, 80);
  eff.phenol        = applyRemoval(q.phenol, 70);

  notes.push(`Lawrence-McCarty kinetics: Se(BOD) = Ks(1+kd·θc) / [θc(µmax-kd)-1] = ${Se_bod.toFixed(1)} mg/L at SRT ${SRT}d, HRT ${HRT_h}h.`);
  notes.push(`Oxygen demand: 1.0 kg O₂/kg BOD + 4.57 kg O₂/kg NH₃-N nitrified = ${(o2Kgd * 1000).toFixed(0)} g O₂/m³.`);
  notes.push(`Net sludge: Px = Y·ΔS - kd·MLVSS = ${(Math.max(0, sludgeKgd) * 1000).toFixed(0)} g VSS/m³·d.`);

  return {
    effluent: eff,
    removals: { bod5: bodRem, cod: codRem, tss: tssRem, ammoniaN: nh3Rem, totalNitrogen: tnRem, totalPhosphorus: tpRem, sulfides: 70, surfactants: 80, phenol: 70 },
    dosing, notes, warnings, sludgeKgd: Math.max(0, sludgeKgd), o2Kgd,
  };
}

// 8. Secondary Clarifier ───────────────────────────────────────────────────
function simSecondary(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const notes: string[] = [];
  const warnings: string[] = [];

  // SOR: 0.5–2.0 m/h. Biomass settles with SVI 80–150 mL/g typical
  const tssRem = q.tss < 3000 ? 90 : 85; // MLSS settles well below 3000 mg/L
  // Residual BOD and COD in settled effluent (soluble)
  const bodRem = 15; const codRem = 10;

  eff.tss  = applyRemoval(q.tss, tssRem);
  eff.bod5 = applyRemoval(q.bod5, bodRem);
  eff.cod  = applyRemoval(q.cod, codRem);

  const sludgeKgd = (q.tss - eff.tss) * 1.5;

  notes.push(`Surface overflow rate (SOR): 0.8–1.5 m/h. Return Activated Sludge (RAS) ratio 0.5–1.0×Q.`);
  notes.push(`Waste Activated Sludge (WAS) controls MLSS and SRT. SVI target 80–150 mL/g for good settling.`);
  if (q.tss > 5000) warnings.push(`MLSS ${q.tss} mg/L very high — risk of sludge blanket overflow. Reduce MLSS or increase clarifier SOR.`);

  return { effluent: eff, removals: { tss: tssRem, bod5: bodRem, cod: codRem }, dosing: [], notes, warnings, sludgeKgd, o2Kgd: 0 };
}

// 9. Multimedia Filter ─────────────────────────────────────────────────────
function simMultimedia(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const notes: string[] = [];
  const warnings: string[] = [];

  // Media: anthracite (top) + sand (bottom) — dual-media
  // Filtration rate 5–15 m/h. Head loss builds until backwash at 200–250 mbar
  const tssRem     = q.tss < 30  ? 80 : q.tss < 100 ? 65 : 50;
  const turbidRem  = 85;
  const bodRem     = q.bod5 < 20  ? 20 : 10;

  if (q.tss > 80) warnings.push(`Influent TSS ${q.tss} mg/L too high for direct multimedia filtration — filter will blind rapidly. Upstream clarification essential.`);

  eff.tss       = applyRemoval(q.tss, tssRem);
  eff.turbidity = applyRemoval(q.turbidity || q.tss * 0.3, turbidRem);
  eff.bod5      = applyRemoval(q.bod5, bodRem);

  notes.push(`Dual-media filter (anthracite d60 = 1.2 mm / sand d60 = 0.5 mm). Operating rate 8–12 m/h.`);
  notes.push(`Backwash with air scour (50 m/h) + water wash (20–25 m/h) at 200–250 mbar head loss.`);

  return { effluent: eff, removals: { tss: tssRem, turbidity: turbidRem, bod5: bodRem }, dosing: [], notes, warnings, sludgeKgd: 0.01, o2Kgd: 0 };
}

// 10. GAC Filter ───────────────────────────────────────────────────────────
// Freundlich isotherm: q = Kf × Ce^(1/n)
// EBCT (empty bed contact time): 10–20 min typical
function simGAC(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const notes: string[] = [];
  const warnings: string[] = [];

  const EBCT_min = 15; // empty bed contact time

  // Adsorption effectiveness (COD): Kf ≈ 35, 1/n ≈ 0.4 (typical)
  // At Ce = COD_in, equilibrium capacity q ∝ Ce^0.4
  // Effective removal decreases as COD increases (capacity per unit volume fixed)
  const codEff_pct = Math.min(75, 40 + EBCT_min * 1.5 - q.cod * 0.05);
  const codRem     = clamp(codEff_pct, 35, 78);
  const bodRem     = clamp(codRem * 0.8, 25, 70);
  const colorRem   = clamp(60 + q.color * 0.15, 65, 96);
  const phenolRem  = clamp(70 + q.phenol * 2, 70, 95);
  const surfRem    = clamp(50 + q.surfactants * 2, 50, 82);

  eff.cod         = applyRemoval(q.cod, codRem);
  eff.bod5        = applyRemoval(q.bod5, bodRem);
  eff.color       = applyRemoval(q.color, colorRem);
  eff.phenol      = applyRemoval(q.phenol, phenolRem);
  eff.surfactants = applyRemoval(q.surfactants, surfRem);

  if (q.tss > 20) warnings.push(`TSS ${q.tss} mg/L will prematurely foul GAC bed. Upstream filtration to <10 mg/L TSS required.`);
  if (q.cod > 150) warnings.push(`High COD ${q.cod} mg/L — GAC bed life severely shortened. Carbon replacement costs elevated. Ensure upstream biological treatment reduces COD < 60 mg/L.`);

  notes.push(`Freundlich isotherm: q_e = K_f × C_e^(1/n). Kf ≈ 35 (mg/g)(L/mg)^(1/n), 1/n ≈ 0.40.`);
  notes.push(`EBCT ${EBCT_min} min at ${EBCT_min * 10} L/m² bed volume. Breakthrough monitoring at 50% of inlet concentration triggers regeneration.`);

  return { effluent: eff, removals: { cod: codRem, bod5: bodRem, color: colorRem, phenol: phenolRem, surfactants: surfRem }, dosing: [], notes, warnings, sludgeKgd: 0, o2Kgd: 0 };
}

// 11. Sludge Holding ───────────────────────────────────────────────────────
function simSludge(q: WaterQuality): SimOut {
  // Sludge holding is a sidestream — no effect on main water quality train
  return {
    effluent: copyWQ(q), removals: {}, dosing: [],
    notes: ['Sludge holding tank consolidates thickened sludge from primary and secondary stages.', 'Supernatant return (reject water) may need treatment for high NH₃-N and TP.'],
    warnings: [], sludgeKgd: 0, o2Kgd: 0,
  };
}

// 12. MBBR (Moving Bed Biofilm Reactor) ────────────────────────────────────
// Biofilm on suspended plastic carriers (50–60% fill). High SRT for nitrifiers
// at short HRT. Loading ≤2 g NH₃-N/m²·d. Needs downstream clarifier for sloughed biofilm.
function simMBBR(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const warnings: string[] = [];
  const bodRem = q.bod5 > 0 ? clamp(80 + (q.bod5 > 400 ? -10 : 5), 70, 90) : 85;
  const codRem = clamp(bodRem * 0.85, 55, 80);
  const nitrify = q.ph >= 6.5 && q.ph <= 8.5;
  const nh3Rem = nitrify ? clamp(82 + q.ammoniaN * 0.1, 82, 95) : 30;
  const tnRem  = nitrify ? 30 : 10;
  const tssRem = 35; // sloughed biofilm — downstream clarifier removes the rest
  const tpRem  = 18;
  if (!nitrify) warnings.push('Nitrification limited — pH outside 6.5–8.5; biofilm nitrifiers inhibited.');
  const bodRemoved = q.bod5 - applyRemoval(q.bod5, bodRem);
  const nh3Nitrified = nitrify ? q.ammoniaN * nh3Rem / 100 : 0;
  eff.bod5 = Math.max(2, applyRemoval(q.bod5, bodRem));
  eff.cod = applyRemoval(q.cod, codRem);
  eff.ammoniaN = applyRemoval(q.ammoniaN, nh3Rem);
  eff.totalNitrogen = applyRemoval(q.totalNitrogen, tnRem);
  eff.tss = applyRemoval(q.tss, tssRem);
  eff.totalPhosphorus = applyRemoval(q.totalPhosphorus, tpRem);
  return {
    effluent: eff,
    removals: { bod5: bodRem, cod: codRem, ammoniaN: nh3Rem, totalNitrogen: tnRem, tss: tssRem, totalPhosphorus: tpRem },
    dosing: [], notes: [
      'Biofilm on plastic carriers (AnoxKaldnes-type, 50–60% fill). High effective SRT decouples solids retention from HRT.',
      'Loading ≤2 g NH₃-N/m²·d for full nitrification. No sludge recycle; compact footprint. Needs downstream clarifier/DAF for sloughed biofilm.',
    ],
    warnings, sludgeKgd: bodRemoved * 0.5 * 0.001, o2Kgd: (bodRemoved * 1.0 + nh3Nitrified * 4.57) * 0.001,
  };
}

// 13. UASB (Upflow Anaerobic Sludge Blanket) ───────────────────────────────
// Anaerobic granular sludge bed. CH₄ recovery. Best for high-strength waste
// (BOD > 500 mg/L). HLR 1–3 m/h, HRT 4–8h. NH₃ is NOT removed (mineralised).
function simUASB(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const warnings: string[] = [];
  const strength = q.bod5;
  const bodRem = clamp(60 + strength * 0.02, 60, 85);
  const codRem = clamp(bodRem * 0.92, 55, 80);
  const tssRem = 60;
  if (strength < 300) warnings.push('UASB best suited to high-strength waste (BOD > 500 mg/L) — low-strength feed gives poor granulation.');
  eff.bod5 = applyRemoval(q.bod5, bodRem);
  eff.cod  = applyRemoval(q.cod, codRem);
  eff.tss  = applyRemoval(q.tss, tssRem);
  const codRemoved = q.cod - eff.cod;
  return {
    effluent: eff,
    removals: { bod5: bodRem, cod: codRem, tss: tssRem },
    dosing: [], notes: [
      'Upflow anaerobic granular sludge blanket. Organics → biogas (≈0.35 m³ CH₄/kg COD removed) — net energy producer.',
      'HLR 1–3 m/h, HRT 4–8h. Effluent still needs aerobic polishing (residual BOD + NH₃). NH₃-N is released, not removed.',
    ],
    warnings, sludgeKgd: codRemoved * 0.08 * 0.001, o2Kgd: 0,
  };
}

// 14. MBR (Membrane Bioreactor) ─────────────────────────────────────────────
// Activated sludge + hollow-fibre/flat-sheet UF (0.04–0.4 µm). Near-zero TSS.
function simMBR(q: WaterQuality): SimOut {
  const eff = copyWQ(q);
  const warnings: string[] = [];
  const nitrify = q.ph >= 6.5 && q.ph <= 8.5;
  const bodRem = 98, codRem = 95, tssRem = 99.5, turbRem = 99;
  const nh3Rem = nitrify ? 95 : 35;
  const tnRem  = nitrify ? 45 : 15;
  const tpRem  = 30;
  if (!nitrify) warnings.push('Nitrification limited — pH outside 6.5–8.5.');
  const bodRemoved = q.bod5 - applyRemoval(q.bod5, bodRem);
  const nh3Nitrified = nitrify ? q.ammoniaN * nh3Rem / 100 : 0;
  eff.bod5 = Math.max(1, applyRemoval(q.bod5, bodRem));
  eff.cod  = Math.max(15, applyRemoval(q.cod, codRem));
  eff.tss  = Math.max(0.5, applyRemoval(q.tss, tssRem));
  eff.turbidity = applyRemoval(q.turbidity || q.tss * 0.3, turbRem);
  eff.ammoniaN = applyRemoval(q.ammoniaN, nh3Rem);
  eff.totalNitrogen = applyRemoval(q.totalNitrogen, tnRem);
  eff.totalPhosphorus = applyRemoval(q.totalPhosphorus, tpRem);
  eff.surfactants = applyRemoval(q.surfactants, 85);
  return {
    effluent: eff,
    removals: { bod5: bodRem, cod: codRem, tss: tssRem, turbidity: turbRem, ammoniaN: nh3Rem, totalNitrogen: tnRem, totalPhosphorus: tpRem, surfactants: 85 },
    dosing: [], notes: [
      'Activated sludge + immersed UF membrane (0.04–0.4 µm) physically retains all biomass — permeate TSS < 1 mg/L, turbidity < 0.2 NTU.',
      'HRT 3–6h, SRT 15–30d, TMP 0.2–0.5 bar. Membrane sets a hard barrier independent of settleability — no secondary clarifier needed.',
    ],
    warnings, sludgeKgd: bodRemoved * 0.45 * 0.001, o2Kgd: (bodRemoved * 1.0 + nh3Nitrified * 4.57) * 0.001,
  };
}

// ─── Main simulation dispatcher ────────────────────────────────────────────

export const STAGE_MAP: Record<string, 'pre-treatment' | 'primary' | 'secondary' | 'tertiary' | 'polishing' | 'sludge'> = {
  screening: 'pre-treatment', eq: 'pre-treatment', ph: 'pre-treatment', buffer: 'pre-treatment',
  daf: 'primary', lamella: 'primary', uasb: 'primary',
  aeration: 'secondary', secondary: 'secondary', mbbr: 'secondary', mbr: 'secondary',
  multimedia: 'tertiary', gac: 'polishing',
  sludge: 'sludge',
};

export const LABEL_MAP: Record<string, string> = {
  screening: 'Screening', eq: 'Equalization Tank', ph: 'pH Adjustment', buffer: 'Buffer Tank',
  daf: 'DAF Unit', lamella: 'Lamella Clarifier', uasb: 'UASB Reactor',
  aeration: 'Aeration Tank', secondary: 'Secondary Clarifier', mbbr: 'MBBR', mbr: 'Membrane Bioreactor (MBR)',
  multimedia: 'Multimedia Filter', gac: 'GAC Filter',
  sludge: 'Sludge Holding',
};

export function simulateUnit(key: string, q: WaterQuality): SimOut {
  switch (key) {
    case 'screening':  return simScreening(q);
    case 'eq':         return simEqualization(q);
    case 'ph':         return simPHAdjustment(q);
    case 'buffer':     return simBuffer(q);
    case 'daf':        return simDAF(q);
    case 'lamella':    return simLamella(q);
    case 'aeration':   return simAeration(q);
    case 'secondary':  return simSecondary(q);
    case 'mbbr':       return simMBBR(q);
    case 'uasb':       return simUASB(q);
    case 'mbr':        return simMBR(q);
    case 'multimedia': return simMultimedia(q);
    case 'gac':        return simGAC(q);
    case 'sludge':     return simSludge(q);
    default: return { effluent: copyWQ(q), removals: {}, dosing: [], notes: [], warnings: [], sludgeKgd: 0, o2Kgd: 0 };
  }
}

// ─── Standards → limits ─────────────────────────────────────────────────────

function getLimits(standardCode: StandardCode): Partial<Record<keyof WaterQuality, number>> {
  const std: WastewaterStandard | undefined = ALL_STANDARDS.find(s => s.code === standardCode);
  if (!std) return {};
  const L = std.limits;
  return {
    bod5: L.bod5, cod: L.cod, tss: L.tss, oilGrease: L.oilGrease,
    ammoniaN: L.ammoniaN, totalNitrogen: L.totalNitrogen, totalPhosphorus: L.totalPhosphorus,
    tds: L.tds, conductivity: L.conductivity,
    lead: L.lead, mercury: L.mercury, cadmium: L.cadmium, chromium: L.chromium,
    nickel: L.nickel, zinc: L.zinc, copper: L.copper,
    phenol: L.phenol, sulfides: L.sulfides, surfactants: L.surfactants,
  };
}

// ─── Compliance check ──────────────────────────────────────────────────────

function checkCompliance(wq: WaterQuality, limits: Partial<Record<keyof WaterQuality, number>>): TrainComplianceResult[] {
  const results: TrainComplianceResult[] = [];
  (Object.keys(limits) as Array<keyof WaterQuality>).forEach(param => {
    const limit = limits[param]!;
    const value = (wq[param] as number) ?? 0;
    if (limit <= 0 || value === 0) return; // skip if no limit set or param not present
    const meta = WQ_META[param];
    const ratio = value / limit;
    const margin = ((limit - value) / limit) * 100;
    const pass = value <= limit;
    const status: TrainComplianceResult['status'] =
      !pass && ratio >= 2    ? 'critical' :
      !pass                  ? 'exceeded' :
      margin < 20            ? 'near_limit' : 'compliant';
    const color =
      status === 'compliant'  ? '#10b981' :
      status === 'near_limit' ? '#f59e0b' :
      status === 'exceeded'   ? '#f97316' : '#ef4444';
    results.push({ param, label: meta.label, unit: meta.unit, finalValue: value, limit, pass, ratio, margin, status, color });
  });
  return results;
}

// ─── Gap analysis & alternative process recommendation ─────────────────────

function buildAlternatives(param: keyof WaterQuality, currentValue: number, limit: number): AlternativeProcess[] {
  const alts: AlternativeProcess[] = [];
  const addAlt = (key: string, name: string, stage: string, rem: number, cap: AlternativeProcess['capexBand'], op: AlternativeProcess['opexBand'], chemistry: string, design: string) => {
    const projected = applyRemoval(currentValue, rem);
    alts.push({ key, name, stage, removalPct: rem, projectedValue: projected, achieves: projected <= limit, chemistry, designNote: design, capexBand: cap, opexBand: op });
  };

  switch (param) {
    case 'bod5':
    case 'cod':
      addAlt('mbr',       'MBR (Membrane Bioreactor)',           'secondary', 98, 'high',      'high',   'Activated sludge + hollow-fibre UF membrane (0.04–0.4 µm). BOD <5, COD <30 mg/L.', 'HRT 3–6h, SRT 15–30d, TMP 0.2–0.5 bar. Near zero TSS in permeate.');
      addAlt('aop',       'Advanced Oxidation (O₃/H₂O₂)',        'tertiary',  70, 'high',      'high',   'O₃ + H₂O₂ → OH• radical (Fenton-like). OH• rate constant 10⁸–10¹⁰ L/mol·s.', 'O₃ dose 5–20 mg/L, contact time 10–30 min. Effective for refractory COD.');
      addAlt('uasb',      'UASB (Anaerobic Reactor)',             'primary',   75, 'medium',    'low',    'Upflow anaerobic sludge blanket. CH₄ recovery. BOD/COD 50–80% removal.', 'HLR 1–3 m/h, HRT 4–8h for high-strength waste (BOD > 500 mg/L).');
      addAlt('lagoon',    'Facultative Lagoon',                   'secondary', 80, 'low',       'low',    'Surface aerators + natural UV, algal photosynthesis. BOD 80% removal with 15–30d HRT.', 'Large land area required. 0.3–1.5 m depth. Simple, low-cost polishing.');
      break;

    case 'tss':
      addAlt('mbr',       'MBR (Membrane Bioreactor)',            'secondary', 99.5, 'high',   'high',   'UF membrane (0.04–0.4 µm) physically retains all suspended solids.', 'Permeate TSS < 1 mg/L guaranteed by membrane size exclusion.');
      addAlt('cf',        'Chemical Coagulation + Filter',        'tertiary',  90,   'medium',  'medium', 'Alum/FeCl₃ destabilise colloids → floc → sand filter. Effective at TSS > 10 mg/L.', 'Coagulant dose 100–300 mg/L alum. Filter rate 5–12 m/h.');
      addAlt('sand',      'Rapid Sand Filter',                    'tertiary',  85,   'low',     'low',    'Depth filtration on sand d60 = 0.5 mm. Remove particles > 5 µm.', 'Loading 5–12 m/h. Backwash 20–25 m/h + air scour. Simple, robust.');
      break;

    case 'ammoniaN':
    case 'totalNitrogen':
      addAlt('nd',        'Nitrification/Denitrification (BNR)',  'secondary', 90, 'medium',   'medium', 'Nitrification: NH₃ + 1.5O₂ → NO₂⁻ + H₂O; NO₂⁻ + 0.5O₂ → NO₃⁻. Denitrification: NO₃⁻ + CH₂O → N₂ + CO₂.', 'Anoxic zone + internal recycle 3–5×Q. SRT > 12d at 20°C for full nitrification.');
      addAlt('mbbr',      'MBBR (Moving Bed Biofilm Reactor)',    'secondary', 85, 'medium',   'medium', 'Biofilm on plastic carriers (50–60% fill). High SRT for nitrifiers at short HRT.', 'Loading ≤2 g NH₃-N/m²·d. No sludge recycle. Compact footprint.');
      addAlt('stripper',  'Ammonia Air Stripping',                'pre-treatment', 92, 'medium','medium', 'NH₃(aq) ⇌ NH₃(g). pKa = 9.25; at pH > 10.5, NH₄⁺ → NH₃(free) → stripped.', 'pH elevate to 11 with NaOH. Counter-current packed tower. NH₃ > 200 mg/L wastewater only.');
      addAlt('ix',        'Ion Exchange (clinoptilolite)',         'tertiary',  95, 'medium',   'medium', 'Zeolite clinoptilolite selectively exchanges NH₄⁺ with Na⁺. Kₛ for NH₄⁺ >> Na⁺.', 'Loading 2–5 BV/h. Regenerate with 1 M NaCl. Effective NH₃ <100 mg/L.');
      break;

    case 'totalPhosphorus':
      addAlt('chemP',     'Chemical P Precipitation (FeCl₃)',     'primary',   95, 'low',      'medium', 'FeCl₃ + PO₄³⁻ → FePO₄↓. Fe:P molar ratio 1.5–2.5:1 for >90% removal.', 'FeCl₃ dose = 5.3 × TP (mg/L) × 1.8 safety factor. Sludge thickening required.');
      addAlt('ebpr',      'EBPR (Enhanced Bio-P Removal)',        'secondary', 85, 'medium',   'low',    'Anaerobic + aerobic cycling triggers polyphosphate-accumulating organisms (PAO). P luxury uptake: 20–30 mg P/g VSS in PAO sludge.', 'Anaerobic HRT 1–2h, SRT > 5d. Volatile fatty acids (VFA) required for PAO enrichment.');
      addAlt('struvite',  'Struvite Crystallisation',             'tertiary',  80, 'high',     'medium', 'MgNH₄PO₄·6H₂O precipitation at pH 8.5–9.5. Mg:N:P = 1:1:1 molar.', 'MgCl₂ or MgO dosing. Fluidised bed reactor (FBR). P removed as marketable slow-release fertiliser.');
      break;

    case 'tds':
    case 'conductivity':
      addAlt('ro',        'Reverse Osmosis',                      'polishing', 97, 'very_high','high',   'Membrane (0.0001 µm) rejection of dissolved salts. Osmotic pressure Δπ = iMRT. Recovery 70–85%.', 'Pre-treatment: SDI < 5, TSS < 0.5 mg/L. Antiscalant dosing. CIP required.');
      addAlt('ed',        'Electrodialysis Reversal (EDR)',        'polishing', 90, 'high',     'high',   'Ion-selective membranes drive ionic migration under electric field. Effective for TDS > 500 mg/L.', 'Stack voltage 1–2 V/cell. Polarity reversal prevents scaling. 80–90% recovery.');
      addAlt('evap',      'Evaporation/Crystallisation',          'polishing', 99, 'very_high','very_high','Phase change: latent heat 2260 kJ/kg water removed. ZLD (zero liquid discharge).', 'MVR (mechanical vapour recompression) or MED (multi-effect distillation). Capital intensive.');
      break;

    case 'lead': case 'mercury': case 'cadmium': case 'chromium':
    case 'nickel': case 'zinc': case 'copper':
      addAlt('precip',    'Chemical Precipitation (lime/NaOH)',   'pre-treatment', 92, 'low', 'low', 'M²⁺ + 2OH⁻ → M(OH)₂↓. Minimum solubility: pH 9.5–11 for most divalent metals.', 'Lime or NaOH to target pH. Flocculation + clarification. Simple, cost-effective.');
      addAlt('sulfide',   'Sulphide Precipitation',              'pre-treatment', 98, 'low', 'medium','MS ↓ precipitation: Hg²⁺ + S²⁻ → HgS↓. Ksp(HgS) = 4×10⁻⁵³ — extremely insoluble.', 'Na₂S or NaHS dosing. Applicable for Hg, Pb, Cd below pH 9. H₂S handling required.');
      addAlt('ix_metal',  'Ion Exchange (chelating resin)',       'polishing',    95, 'high','medium', 'Chelating iminodiacetic acid resin: Cu²⁺, Ni²⁺, Zn²⁺ preferentially bound over Na⁺, Ca²⁺.', 'Low metal concentrations (<5 mg/L). Acid regeneration. Effective to µg/L levels.');
      addAlt('ro',        'Reverse Osmosis',                      'polishing',    97, 'very_high','high','Membrane rejection of multivalent ions > 95–99%. RO permeate < 0.05 mg/L most metals.', 'Pre-treat to TSS < 0.5 mg/L. Antiscalant required if Ca/Mg high.');
      break;

    case 'phenol':
      addAlt('aop',       'Advanced Oxidation (O₃/H₂O₂)',        'tertiary',  90, 'high',    'high',   'Phenol oxidised by OH•: C₆H₅OH + 14OH• → 6CO₂ + 8H₂O. k_OH = 8.8×10⁹ M⁻¹s⁻¹.', 'O₃ 10–20 mg/L + H₂O₂ 0.5× O₃ molar. Contact time 15–30 min. pH 7–8 optimal.');
      addAlt('bioaug',    'Bioaugmentation with adapted seed',    'secondary', 85, 'low',     'low',    'Phenol-degrading organisms: Pseudomonas putida (catechol pathway). Inducible enzyme: phenol hydroxylase.', 'Inoculate with acclimatised biomass. HRT > 12h for phenol < 100 mg/L. Intermittent loading.');
      break;

    case 'oilGrease':
      addAlt('daf2',      'Enhanced DAF with Coagulant',          'primary',   96, 'medium',  'medium', 'Coagulation (alum/FeCl₃) + DAF. Emulsified O&G demulsified then floated.', 'pH 6–7 optimal for coag. Polymer 2–5 mg/L. Achieves O&G < 5 mg/L.');
      addAlt('api',       'API Separator (gravity)',              'primary',   70, 'low',     'low',    "Stokes' Law separation of free oil (ρ < 1000 kg/m³). Surface skimmer removes floating oil.", 'SOR 0.3 m/min. Plate pack version: CPI. Free oil removal only — emulsified oil requires chemical treatment.');
      break;

    case 'color':
      addAlt('aop',       'Advanced Oxidation (O₃)',              'tertiary',  95, 'high',    'high',   'Ozone attacks chromophore groups (C=C, C=N) in dye molecules. Decolourisation rapid, but mineralisation requires H₂O₂.', 'O₃ 5–15 mg/L at pH 7–9. Contact 10–20 min. Colour removal 90–99%.');
      addAlt('adsorption','PAC/GAC Adsorption',                   'polishing', 85, 'low',     'medium', 'Coloured compounds (dyes, humic acids) adsorb on activated carbon surface. High surface area: 800–1200 m²/g.', 'PAC dose 20–100 mg/L in slurry, or GAC fixed bed EBCT 15–20 min.');
      addAlt('coag',      'Enhanced Coagulation',                 'primary',   70, 'low',     'medium', 'Alum/FeCl₃ coprecipitation of colour compounds. Effective for humic/fulvic acids.', 'Jar test to optimise dose 100–400 mg/L. pH 5.5–6.5 optimal for colour removal.');
      break;

    case 'sulfides':
      addAlt('aer_ph',    'Aeration + pH Elevation',              'pre-treatment', 95, 'low','low', 'H₂S(aq) + ½O₂ → S⁰ + H₂O (catalytic). At pH > 9: HS⁻ dominant, less volatile. S⁰ → SO₄²⁻ further oxidation.', 'Diffused air at 0.1–0.2 m³ air/m³·min. pH > 8.5 reduces H₂S gas emission risk.');
      addAlt('fe_dosing', 'Iron Salt Precipitation (FeSO₄)',      'pre-treatment', 90, 'low','low', 'Fe²⁺ + S²⁻ → FeS↓ (Ksp = 4×10⁻¹⁹). Rapid, prevents H₂S gas generation.', 'FeSO₄ dose = 1.75 × sulfide (mg/L). pH 7–9. Sludge increased proportionally.');
      break;

    default:
      addAlt('pol',  'Advanced Polishing (site-specific)',        'polishing', 80, 'medium', 'medium', 'Parameter-specific treatment. Consult specialist for bespoke design.', 'Multiple processes may combine for target parameter.');
  }

  // Sort: achieves compliance first, then by removal %
  return alts.sort((a, b) => {
    if (a.achieves !== b.achieves) return a.achieves ? -1 : 1;
    return b.removalPct - a.removalPct;
  });
}

// ─── Master simulation function ────────────────────────────────────────────

export function simulateTrain(
  rawParams:     Record<string, number>,
  equipmentKeys: string[],
  standardCode:  StandardCode,
): TrainSimulation {

  const std = ALL_STANDARDS.find(s => s.code === standardCode);
  const standardName = std?.name ?? standardCode;
  const limits = getLimits(standardCode);

  // Build influent
  const influent = rawParamsToWQ(rawParams);

  // Skip sludge in the main water quality chain (it's a sidestream)
  const mainChain = equipmentKeys.filter(k => k !== 'sludge');
  const hasSludge = equipmentKeys.includes('sludge');

  let currentWQ = copyWQ(influent);
  const stages: StageResult[] = [];
  let totalSludgeKgd = 0;
  let totalO2Kgd = 0;
  const allDosing: ChemicalDosing[] = [];
  const allWarnings: string[] = [];

  // Simulate each unit
  for (const key of mainChain) {
    const out = simulateUnit(key, currentWQ);
    stages.push({
      equipmentKey:    key,
      equipmentLabel:  LABEL_MAP[key] ?? key,
      stage:           STAGE_MAP[key] ?? 'pre-treatment',
      influent:        copyWQ(currentWQ),
      effluent:        out.effluent,
      removals:        out.removals,
      dosing:          out.dosing,
      oxygenDemandKgd: out.o2Kgd,
      sludgeKgd:       out.sludgeKgd,
      engineeringNotes: out.notes,
      warnings:        out.warnings,
    });
    totalSludgeKgd += out.sludgeKgd;
    totalO2Kgd     += out.o2Kgd;
    out.dosing.forEach(d => { if (!allDosing.find(x => x.chemical === d.chemical)) allDosing.push(d); });
    allWarnings.push(...out.warnings);
    currentWQ = out.effluent;
  }

  // Sludge holding as informational last stage
  if (hasSludge) {
    const sOut = simSludge(currentWQ);
    stages.push({
      equipmentKey: 'sludge', equipmentLabel: 'Sludge Holding', stage: 'sludge',
      influent: copyWQ(currentWQ), effluent: copyWQ(currentWQ),
      removals: {}, dosing: [], oxygenDemandKgd: 0, sludgeKgd: 0,
      engineeringNotes: sOut.notes, warnings: sOut.warnings,
    });
  }

  const finalEffluent = currentWQ;

  // Compliance check
  const compliance = checkCompliance(finalEffluent, limits);
  const passing    = compliance.filter(r => r.pass).length;
  const score      = compliance.length > 0 ? Math.round((passing / compliance.length) * 100) : 100;
  const overallCompliant = compliance.every(r => r.pass);

  // Gap analysis
  const gaps: ComplianceGap[] = compliance
    .filter(r => !r.pass)
    .map(r => {
      const exceedance    = r.finalValue - r.limit;
      const exceedancePct = (exceedance / r.limit) * 100;
      const requiredRem   = ((r.finalValue - r.limit) / r.finalValue) * 100;
      const alternatives  = buildAlternatives(r.param, r.finalValue, r.limit);
      return { param: r.param, label: r.label, unit: r.unit, finalValue: r.finalValue, limit: r.limit, exceedance, exceedancePct, requiredRemoval: requiredRem, alternatives };
    });

  // Summary
  const summary = overallCompliant
    ? `✓ Train achieves ${standardName} compliance — ${compliance.length} parameters checked, all within limits.`
    : `✗ ${gaps.length} parameter${gaps.length > 1 ? 's' : ''} exceed ${standardName} limits after full treatment train. Supplementary processes required.`;

  return { influent, stages, finalEffluent, standardCode, standardName, compliance, overallCompliant, complianceScore: score, gaps, totalSludgeKgd, totalO2Kgd, totalDosingChems: allDosing, warnings: [...new Set(allWarnings)], summary };
}

// ─── Parameter display helpers ─────────────────────────────────────────────

export function fmtWQParam(value: number, param: keyof WaterQuality): string {
  if (value === 0) return '0';
  if (value < 0.01) return value.toExponential(1);
  if (value < 1) return value.toFixed(3);
  if (value < 10) return value.toFixed(2);
  return Math.round(value).toLocaleString();
}

export const CAPEX_LABELS: Record<AlternativeProcess['capexBand'], string> = {
  low: '$ Low', medium: '$$ Medium', high: '$$$ High', very_high: '$$$$ Very High',
};
